/* @gm/gmwc-core - 1.7.9 - Wed, 20 Feb 2019 17:43:57 GMT */
/*!

Copyright (C) 2014-2016 by Andrea Giammarchi - @WebReflection

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.

*/


/**
 * @class EventMap
 * @description provides an event bus for when properties of the map change.
 */
class EventMap {
  constructor() {
    this._map = new Map();
    this._subscribers = {
      clear: [],
      'delete': [],
      set: []
    };
    this._cache = {};
  }

  _notify(event, oldState) {
    let subs = this._subscribers[event].slice();
    subs.forEach(sub => sub(event, this._cache, oldState));
  }

  _updateCache() {
    const params = {};
    this._map.forEach((val, key) => params[key] = val);
    this._cache = params;
  }

  /**
   * @memberOf EventMap
   * @param {String} event 'set'= after the property has been set
   * @param {Function} callback notification channel
   */
  on(event, callback) {
    if (!this._subscribers[event]) {
      this._subscribers[event] = [];
    }
    this._subscribers[event].push(callback);
    const subs = this._subscribers[event];

    callback && callback('set', this._cache, {});
    return {
      subscriber: callback,
      event: event,
      destroy: () => {
        subs.splice(subs.indexOf(callback), 1);
      }
    };
  }

  /**
   * @memberOf EventMap
   * @param {String} key
   * @param {Object|Array} val
   * @param {Boolean} notify. set false to NOT notify subscribers.
   */
  set(key, val, notify) {
    let proceed;
    if (Array.isArray(val)) {
      //this might need to be a better algorithm, this should work for now.
      proceed = JSON.stringify(val) !== JSON.stringify(this._cache[key]);
    } else {
      proceed = val !== this._cache[key];
    }
    if (proceed) {
      this._map.set(key, val);
      if (notify !== false) {
        this._notify('set', this._cache);
      }
      this._updateCache();
    }
    return this;
  }

  /**
   * @memberOf EventMap
   * @param {Object} keyValuePairs
   * @param {Boolean} notify. set false to NOT notify subscribers.
   */
  replace(keyValuePairs, notify) {
    const oldState = Object.assign({}, this._cache);
    Object.keys(oldState).forEach(oldKey => this.set(oldKey, keyValuePairs[oldKey], false));
    Object.keys(keyValuePairs).forEach(newKey => this.set(newKey, keyValuePairs[newKey], false));
    if (notify !== false) {
      this._notify('set', oldState);
    }
    return this;
  }

  /**
   * @memberOf EventMap
   * @returns object hash of all the key value pairs.
   */
  getAll() {
    return this._cache;
  }

  get(key) {
    return this._map.get(key);
  }
  entries() {
    return this._map.entries();
  }
  forEach(callback) {
    return this._map.forEach(callback);
  }
  keys() {
    return this._map.keys();
  }
  values() {
    return this._map.values();
  }
}

var ConfigManager = (() => {
  /**
   *
   * @class ConfigManager
   * @description Configuration values can be set and propagated to consuming components via this static class or through
   * the corresponding gmwc-config element
   *
   */
  const ConfigManager = new class {
    constructor() {
      this._properties = new EventMap();
    }

    /**
     * @memberOf ConfigManager
     * @param callback is the function to execute when any property changes.
     * @returns {{destroy}|*}
     * @description call destroy() on the returned object to remove the event listener.
     */
    onChange(callback) {
      return this._properties.on('set', callback);
    }

    /**
     * @memberOf ConfigManager
     * @param key the name of the value to set. can also be called with an {} query to set multiple values at once.
     * @param value the value of the property to set it to.
     */
    set(key, value) {
      let query = key;
      if (value) {
        //we have a single value
        query = {};
        query[key] = value;
      }
      this._properties.replace(Object.assign({}, this._properties.getAll(), query));
    }

    /**
     * @memberOf ConfigManager
     * @param propertyNames the property names to be notified when they mutate
     * @param callback the callback to be executed when any of the propertyNames have changed.
     * @returns {{destroy}|*}
     * @description call destroy() on the returned object to remove the event listener.
     */
    onPropertyChange(propertyNames, callback) {
      propertyNames = typeof propertyNames === 'string' ? [propertyNames] : propertyNames;
      return this.onChange((event, newConfig, oldConfig) => {
        let updates;
        propertyNames.forEach(property => {
          if (newConfig[property] !== oldConfig[property]) {
            updates = updates || {};
            updates[property] = {
              oldValue: oldConfig[property],
              newValue: newConfig[property]
            };
          }
        });
        updates && callback(updates, newConfig, oldConfig);
      });
    }
  }();
  return ConfigManager;
})();

/**
 * Converts an Object of String/Value pairs to a query string for URL parameters prepended with the "base" character.
 * Encodes unsafe url characters to url safe encodings.
 * @param {Object} options
 * @param {String} base
 *
 * @example <caption>convert object to query string</caption>
 *
 * import {toQueryString} from '@gm/utils';
 * let queryString = toQueryString({
 *  foo: 'bar',
 *  hello: ['world', 'array'],
 *  unsafe: 'I am an unsafe string'
 *
 * }, '#');
 *
 * queryString == '#?foo=bar&hello=world&hello=array&unsafe=I%20am%20an%20unsafe%20string';
 */
const toQueryString = (options, base) => {
  const filtered = Object.entries(options).filter(ent => !!ent[1]);
  base = base || '#';
  const hashBase = filtered.length > 0 ? base + '?' : base;
  return encodeURI(`${hashBase}${filtered.map(ent => {
    if (Array.isArray(ent[1])) {
      return ent[1].map(val => [ent[0], val].join('=')).join('&');
    } else {
      return ent.join('=');
    }
  }).join('&')}`);
};

/**
 * Converts URL parameters to a Object collection of key/value pairs
 * Decodes encoded url characters to back to normal strings.
 * @param {String} str
 *
 * @example <caption>convert query string to object:</caption>
 * import {toParams} from '@gm/utils';
 * let paramsObject = toParams('#?foo=bar&hello=world&hello=array&unsafe=I%20am%20an%20unsafe%20string');
 *
 * paramsObject == {
 *  foo: 'bar',
 *  hello: ['world', 'array'],
 *  unsafe: 'I am an unsafe string'
 *
 * };
 */
const toParams = str => {
  const tempstr = str.split('#', 2);
  if (tempstr[1]) {
    str = tempstr[1];
  }
  const parts = str.split('?');
  const queryString = parts[1] || '';
  const params = {};
  queryString.split('&').forEach(val => {
    const innerParts = val.split('=');
    if (innerParts.length !== 2) return;
    const paramKey = decodeURIComponent(innerParts[0]);
    const paramVal = decodeURIComponent(innerParts[1]);
    let current = params[paramKey];
    if (current) {
      if (!Array.isArray(current)) {
        current = [current];
      }
      current.push(paramVal);
    } else {
      current = paramVal;
    }
    params[paramKey] = current;
  });
  return params;
};

var StateManager = (() => {
  /**
   *
   * @class StateManager
   * @description State Parameters values can be set and propagated to consuming components via this static class or through
   * the corresponding gmwc-defaults element. State Parameters are typically stored in the URL as query string parameters.
   * Although, you can turn that off by setting the 'nourl' value in the @link ConfigManager
   *
   */
  const StateManager = new class {
    constructor() {
      this._base = '#';
      let oldUrl = window.location.href;
      this._params = new EventMap();
      this._validation = {};
      this._parsers = {};
    }

    _updateParams(newParams) {
      const updates = Object.entries(newParams);
      updates.forEach(([key, val], index) => {
        if (!val || val === "undefined" || val === "null") {
          newParams[key] = '';
        }
        this._params.set(key, val, false);
      });
    }

    _setUrlFromState(newState) {
      const urlState = toQueryString(newState, this._base);
      if (urlState !== window.location.hash) {
        window.location.replace(urlState);
      }
    }

    _validate(query) {
      return new Promise((success, error) => {
        const validParams = {};
        const invalidParams = {};
        const waiting = [];
        const currentState = this.get();
        Object.entries(query).forEach(([key, val]) => {
          val = Array.isArray(val) && val.length == 0 ? undefined : val;
          const parsedVal = this._parsers[key] ? this._parsers[key](val, currentState) : val;
          if (this._validation[key]) {
            this._validation[key].forEach(validator => {
              const result = validator.call(this, parsedVal, currentState);
              waiting.push(Promise.resolve(result).then(validity => {
                if (validity === false) {
                  invalidParams[key] = parsedVal;
                  validParams[key] = this.getParam(key);
                } else {
                  validParams[key] = parsedVal;
                }
                return !!validity;
              }));
            });
          } else {
            validParams[key] = parsedVal;
            waiting.push(Promise.resolve(true));
          }
        });
        Promise.all(waiting).then(results => {
          const valid = results.indexOf(false) === -1;
          if (valid) {
            return success({ validParams });
          } else {
            return error({ validParams: validParams, invalidParams: invalidParams });
          }
        });
      });
    }

    /**
     * @memberOf StateManager
     * @param param optional. If you call it with no parameter you will get whatever the entire current state is.
     * @returns {Object} the current value of the requested property name or the whole state object.
     */
    get(param = null) {
      return param ? this._params.get(param) : this._params.getAll();
    }

    /**
     * @memberOf StateManager
     * @param query should be called with an {} query to set multiple values at once.
     */
    set(query) {
      if (arguments.length > 1) {
        return this.setParam.apply(this, arguments);
      }
      const oldState = this.get();
      return new Promise((resolve, reject) => {
        this._validate(query).then(validState => {
          this._updateParams(validState.validParams);
          this._params._notify('set', oldState);
          if (!ConfigManager.get('nourl')) {
            const newStateObj = Object.assign({}, toParams(window.location.href), validState.validParams);
            this._setUrlFromState(newStateObj);
          }
          resolve({ validState, query });
        }, (currentState, query) => {
          const error = new RangeError('One or more parameters did not pass validation.', this);
          console.error(error, query, this._validation);
          reject({ currentState, query, error });
        }, result => reject(result));
      });
    }

    /**
     * @memberOf StateManager
     * @deprecated
     * @param key the state parameter to get
     * @returns the current state value
     */
    getParam(key) {
      return this.get(key);
    }

    /**
     * @memberOf StateManager
     * @deprecated
     * @param key the state parameter to change
     * @param val the value the state parameter should be set to.
     * @returns {Promise} promise returned from set()
     */
    setParam(key, val) {
      const query = {};
      query[key] = val;
      return this.set(query);
    }
  }();
  return StateManager;
})();

/**
 * @class GmwcConfig
 * @extends HTMLScriptElement
 * @name gmwc-config
 * @description sets config property defaults. use the data-prefix on attributes to set the default values.
 * @example
 * <script is="gmwc-config" data-some-config="some-default-value"><script>
 */
const GmwcConfig = (() => {
  class config extends HTMLElement {

    constructor(self) {
      self = super(self);
      return self;
    }

    connectedCallback() {
      ConfigManager.set(Object.keys(this.dataset).reduce((accu, key) => {
        accu[key] = this.dataset[key];
        return accu;
      }, {}));
    }
  }
  customElements.define('gmwc-config', config);
  return config;
})();

export { StateManager,  ConfigManager,  GmwcConfig };
